import ApiService from './apiService';

/**
 * User Groups Service
 * Handles user group management
 */
export interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'manager' | 'user' | 'viewer';
  avatar?: string;
  status?: 'active' | 'inactive';
  firstName: string;
  lastName: string;
  department?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UserGroup {
  id: string;
  name: string;
  description?: string;
  members: User[];
  createdAt: string;
  updatedAt: string;
  organizationId?: string;
  permissions?: string[];
  isActive?: boolean;
}

const getAuthPayload = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  if (!token) {
    return null;
  }

  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
};

const getCurrentOrganizationId = () => {
  const payload = getAuthPayload();
  return payload?.organizationId || payload?.organizations?.[0]?.organizationId;
};

const splitName = (value?: string) => {
  const cleaned = (value || 'User').replace(/[._-]+/g, ' ').trim();
  const parts = cleaned.split(/\s+/);
  return {
    firstName: parts[0] || 'User',
    lastName: parts.slice(1).join(' ') || 'Member',
  };
};

const normalizeUser = (user: any): User => {
  const baseName = user?.name || user?.username || user?.email || 'User';
  const { firstName, lastName } = splitName(baseName);

  return {
    id: user?.id,
    name: baseName,
    email: user?.email || '',
    role: user?.role || 'user',
    firstName,
    lastName,
    department: user?.department || '',
    isActive: user?.isActive ?? true,
    createdAt: user?.createdAt || user?.created_at || new Date().toISOString(),
    updatedAt: user?.updatedAt || user?.updated_at || new Date().toISOString(),
  };
};

const normalizeGroup = (group: any): UserGroup => ({
  id: group.id,
  name: group.name,
  description: group.description,
  members: Array.isArray(group.members)
    ? group.members.map((member: any) => normalizeUser(member.user || member))
    : [],
  createdAt: group.createdAt || group.created_at || new Date().toISOString(),
  updatedAt: group.updatedAt || group.updated_at || new Date().toISOString(),
  organizationId: group.organizationId || group.organization_id || group.organization?.id,
  permissions: Array.isArray(group.permissions) ? group.permissions : [],
  isActive: group.isActive ?? true,
});

export const UserGroupsService = {
  getUserGroups: async () => {
    const organizationId = getCurrentOrganizationId();
    const response = await ApiService.get<any>('/user-groups', {
      params: organizationId ? { organizationId } : undefined,
    });

    const groups = Array.isArray(response) ? response : response?.groups || [];
    return { groups: groups.map(normalizeGroup) };
  },

  getUsers: async () => {
    const response = await ApiService.get<any[]>('/users');
    const users = Array.isArray(response) ? response : [];
    return { users: users.map(normalizeUser) };
  },

  getUserGroup: async (id: string) => {
    const response = await ApiService.get<any>(`/user-groups/${id}/with-members`);
    return normalizeGroup(response);
  },

  createUserGroup: async (data: {
    name: string;
    description?: string;
    memberIds?: string[];
    members?: string[];
    permissions?: string[];
    organizationId?: string;
  }) => {
    const organizationId = data.organizationId || getCurrentOrganizationId();
    if (!organizationId) {
      throw new Error('No organization selected for this user group');
    }

    const memberIds = data.memberIds || data.members || [];
    const createdGroup = await ApiService.post<any>('/user-groups', {
      name: data.name,
      description: data.description,
      organization_id: organizationId,
      admin_id: memberIds[0],
    });

    if (memberIds.length > 0) {
      await Promise.all(
        memberIds.map(userId =>
          ApiService.post(`/user-groups/${createdGroup.id}/members`, {
            user_id: userId,
            role: userId === memberIds[0] ? 'admin' : 'member',
          }).catch(() => null)
        )
      );
    }

    return UserGroupsService.getUserGroup(createdGroup.id);
  },

  updateUserGroup: async (id: string, updates: Partial<UserGroup> & { memberIds?: string[] }) => {
    const response = await ApiService.put<any>(`/user-groups/${id}`, {
      name: updates.name,
      description: updates.description,
    });
    return normalizeGroup(response);
  },

  deleteUserGroup: async (id: string) => {
    return ApiService.delete(`/user-groups/${id}`);
  },

  addMember: async (groupId: string, userId: string) => {
    return ApiService.post(`/user-groups/${groupId}/members`, {
      user_id: userId,
      role: 'member',
    });
  },

  removeMember: async (groupId: string, userId: string) => {
    return ApiService.delete(`/user-groups/${groupId}/members/${userId}`);
  },

  getMembers: async (groupId: string) => {
    const response = await ApiService.get<any>(`/user-groups/${groupId}/members`);
    const members = Array.isArray(response) ? response : response?.members || [];
    return { members: members.map((member: any) => normalizeUser(member.user || member)) };
  },

  assignPermissions: async (_groupId: string, permissions: string[]) => {
    return Promise.resolve({ permissions });
  },

  searchUsers: async (query: string) => {
    const { users } = await UserGroupsService.getUsers();
    const normalizedQuery = query.trim().toLowerCase();
    return {
      users: users.filter(user =>
        !normalizedQuery ||
        user.name?.toLowerCase().includes(normalizedQuery) ||
        user.email?.toLowerCase().includes(normalizedQuery)
      ),
    };
  },

  bulkAddMembers: async (groupId: string, userIds: string[]) => {
    return Promise.all(userIds.map(userId => UserGroupsService.addMember(groupId, userId)));
  },

  exportMembers: async (groupId: string, format: 'csv' | 'excel') => {
    return ApiService.get(`/user-groups/${groupId}/export/${format}`, {
      responseType: 'blob'
    });
  },
};

export default UserGroupsService;
